import { NavLink, Link, Outlet } from "react-router-dom";
import { AuthNav } from "../components/auth-nav";
import { ThemeToggle } from "../components/theme-toggle";

type DashboardLayoutProps = {
  title: string;
  mode?: "admin" | "sales";
};

type NavSection = {
  title: string;
  items: Array<{
    label: string;
    to: string;
    badge?: string;
  }>;
};

const adminSections: NavSection[] = [
  {
    title: "Workspace",
    items: [
      { label: "Overview", to: "/admin" },
      { label: "Catalog", to: "/admin/catalog" },
      { label: "Orders", to: "/admin/orders" },
      { label: "Suppliers", to: "/admin/suppliers" },
      { label: "Purchasing", to: "/admin/purchasing" }
    ]
  },
  {
    title: "Operations",
    items: [
      { label: "Users", to: "/admin/users" },
      { label: "Coupons", to: "/admin/coupons" },
      { label: "Projects", to: "/admin/projects" },
      { label: "Reports", to: "/admin/reports" }
    ]
  },
  {
    title: "System",
    items: [{ label: "Settings", to: "/admin/settings" }]
  }
];

const salesSections: NavSection[] = [
  {
    title: "Workspace",
    items: [
      { label: "Overview", to: "/sales" },
      { label: "Orders", to: "/sales/orders" },
      { label: "Payments", to: "/sales/payments" },
      { label: "Catalog", to: "/sales/catalog" }
    ]
  },
  {
    title: "Customers",
    items: [
      { label: "Customers", to: "/sales/customers" },
      { label: "Coupons", to: "/sales/coupons" },
      { label: "Invoices", to: "/sales/invoices" }
    ]
  },
  {
    title: "Account",
    items: [{ label: "Profile", to: "/sales/profile" }]
  }
];

export function DashboardLayout({ title, mode = "sales" }: DashboardLayoutProps) {
  const navSections = mode === "admin" ? adminSections : salesSections;

  return (
    <div className="admin-shell">
      <div className="grid min-h-screen lg:h-screen lg:max-h-screen grid-cols-1 overflow-hidden lg:grid-cols-[290px_minmax(0,1fr)]">
        <aside className="admin-sidebar overflow-y-auto px-5 py-6 lg:px-6">
          <div className="admin-card rounded-[28px] p-4">
            <div className="flex items-center gap-3">
              <img alt="Novatech" className="h-14 w-auto object-contain" src="/logo/logo.jpg" />
              <div>
                <span className="block text-xl font-semibold tracking-tight text-nv-ink">Novatech</span>
                <span className="block text-xs text-nv-slate">Innovate, Enhance and Sustain</span>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <AuthNav compact dark />
          </div>
          <div className="mt-8 space-y-6">
            {navSections.map((section) => (
              <div key={section.title}>
                <p className="admin-nav-section px-3 text-[11px] font-semibold uppercase tracking-[0.24em]">{section.title}</p>
                <nav className="mt-3 space-y-2 text-sm">
                  {section.items.map((item) => (
                    <NavLink
                      className={({ isActive }) =>
                        `admin-nav-link flex items-center justify-between rounded-2xl px-4 py-3 transition ${
                          isActive ? "admin-nav-link--active" : ""
                        }`
                      }
                      key={item.to}
                      to={item.to}
                    >
                      <span>{item.label}</span>
                      {item.badge ? (
                        <span className="rounded-full border border-black/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]">
                          {item.badge}
                        </span>
                      ) : null}
                    </NavLink>
                  ))}
                </nav>
              </div>
            ))}
          </div>
          <div className="admin-card mt-8 rounded-[28px] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300">Quick exit</p>
            <Link className="admin-nav-link mt-3 block rounded-2xl px-4 py-3 text-sm font-medium transition" to="/">
              Back to site
            </Link>
          </div>
        </aside>
        <section className="admin-panel min-w-0 overflow-y-auto p-6 lg:p-8">
          <div className="admin-toolbar mb-6 flex flex-col gap-4 rounded-[28px] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-nv-slate">{mode === "admin" ? "Admin interface" : "Sales interface"}</p>
              <p className="mt-1 text-lg font-semibold tracking-tight text-nv-ink">{title}</p>
            </div>
            <div className="flex items-center gap-3 self-start sm:self-auto">
              <span className="hidden text-sm text-nv-slate sm:inline">Theme</span>
              <ThemeToggle />
            </div>
          </div>
          <Outlet />
        </section>
      </div>
    </div>
  );
}
